import { AvailableLanguages } from '@repo/toolkit/language-types'
import { TFunction } from 'i18next'
import { z, ZodType } from 'zod'

import { BillingTypeEnum } from '../../../lib/types/enums/cse/billing-type.enum'
import { CseInput, CseRequest, CseTypes } from '../../../lib/types/models/cse/cse'
import { createEnumSchema } from '../../../lib/utils/validations/enum-validation'
import { FILE_VALIDATION } from '../../../lib/utils/validations/file.validation'
import { createNumberSchema } from '../../../lib/utils/validations/int-array-validation'
import { PHONE_NUMBER_SCHEMA } from '../../../lib/utils/validations/phone-number.validation'
import { createStringSchema } from '../../../lib/utils/validations/string-validation'

// Partner code format: 1 number + 3 capital letters + 2 numbers (e.g., 1EDM69)
export const PARTNER_CODE_REGEX = /^[0-9][A-Z]{3}[0-9]{2}$/

export const CSE_FORM_SCHEMA = (t: TFunction<AvailableLanguages, undefined>): ZodType<CseInput> =>
  z.object({
    identifier: createStringSchema('Identifiant'),
    isActive: z.boolean(),
    label: createStringSchema('Libellé'),
    code: createStringSchema('Code partenaire').regex(PARTNER_CODE_REGEX, {
      message:
        'Le code partenaire doit être au format: 1 chiffre, 3 lettres majuscules, 2 chiffres (ex: 1EDM69)'
    }),
    logo: FILE_VALIDATION().nullish(),
    address1: createStringSchema('Adresse'),
    address2: createStringSchema("Complément d'adresse", { nullish: true }),
    zipCode: createStringSchema('Code postal'),
    city: createStringSchema('Ville'),
    country: createStringSchema('Pays'),
    email: createStringSchema('Email', { nullish: true, isEmail: true }),
    billingType: createEnumSchema({
      fieldName: 'Type de facturation',
      enumType: BillingTypeEnum,
      nullish: true
    }),
    numberOfEmployees: createNumberSchema('Nombre de salariés', { nullish: true }),
    councilAllowance: z.boolean(),
    offerCancellationInsurance: z.boolean().optional(),
    cancellationInsuranceMandatory: z.boolean().optional(),
    cancellationInsuranceOffered: z.boolean().optional(),
    localTransportCostsOffered: z.boolean().optional(),
    repatriationInsuranceMandatory: z.boolean().optional(),
    repatriationInsuranceOffered: z.boolean().optional(),
    extraOptionEnabled: z.boolean().optional(),
    extraOptionLabel: createStringSchema('Nom du champ', { nullish: true }),
    phoneNumber: PHONE_NUMBER_SCHEMA(t).nullish(),
    canReceiveEmails: z.boolean().optional(),
    comments: createStringSchema('Commentaires', { nullish: true }),
    numberOfRegistrationsCurrentYear: createNumberSchema("Nombre d'inscriptions N", {
      nullish: true,
      isAtLeastZero: true,
      isInteger: true
    }),
    numberOfRegistrationsPreviousYear: createNumberSchema("Nombre d'inscriptions N - 1", {
      nullish: true,
      isAtLeastZero: true,
      isInteger: true
    }),
    numberOfRegistrationsTwoYearsAgo: createNumberSchema("Nombre d'inscriptions N - 2", {
      nullish: true,
      isAtLeastZero: true,
      isInteger: true
    })
  })

export const mapCseToInput = (cse: CseTypes.Cse): CseInput => {
  return {
    identifier: cse.identifier,
    isActive: cse.isActive,
    label: cse.label,
    code: cse.code,
    logo: cse.logo,
    address1: cse.address1,
    address2: cse.address2,
    zipCode: cse.zipCode,
    city: cse.city,
    country: cse.country,
    email: cse.email,
    billingType: cse.billingType,
    numberOfEmployees: cse.numberOfEmployees,
    councilAllowance: cse.councilAllowance,
    offerCancellationInsurance: cse.offerCancellationInsurance,
    cancellationInsuranceMandatory: cse.cancellationInsuranceMandatory,
    cancellationInsuranceOffered: cse.cancellationInsuranceOffered,
    localTransportCostsOffered: cse.localTransportCostsOffered,
    repatriationInsuranceMandatory: cse.repatriationInsuranceMandatory,
    repatriationInsuranceOffered: cse.repatriationInsuranceOffered,
    extraOptionEnabled: cse.extraOptionEnabled,
    extraOptionLabel: cse.extraOptionLabel,
    phoneNumber: cse.phoneNumber,
    canReceiveEmails: cse.canReceiveEmails,
    comments: cse.comments,
    numberOfRegistrationsCurrentYear: cse.registrationYears.find(
      (year) => year.year === new Date().getFullYear()
    )?.numberOfRegistrations,
    numberOfRegistrationsPreviousYear: cse.registrationYears.find(
      (year) => year.year === new Date().getFullYear() - 1
    )?.numberOfRegistrations,
    numberOfRegistrationsTwoYearsAgo: cse.registrationYears.find(
      (year) => year.year === new Date().getFullYear() - 2
    )?.numberOfRegistrations
  }
}

export const mapCseInputToRequest = (input: CseInput): CseRequest => {
  const registrationYears = []
  const {
    numberOfRegistrationsCurrentYear,
    numberOfRegistrationsPreviousYear,
    numberOfRegistrationsTwoYearsAgo,
    ...rest
  } = input
  if (numberOfRegistrationsCurrentYear !== undefined)
    registrationYears.push({
      year: new Date().getFullYear(),
      numberOfRegistrations: numberOfRegistrationsCurrentYear ?? 0
    })

  if (numberOfRegistrationsPreviousYear !== undefined)
    registrationYears.push({
      year: new Date().getFullYear() - 1,
      numberOfRegistrations: numberOfRegistrationsPreviousYear ?? 0
    })

  if (numberOfRegistrationsTwoYearsAgo !== undefined)
    registrationYears.push({
      year: new Date().getFullYear() - 2,
      numberOfRegistrations: numberOfRegistrationsTwoYearsAgo ?? 0
    })

  return { ...rest, numberOfRegistrations: registrationYears }
}
